import { API_CONFIG } from '@/lib/api/config';
import { ApiError } from '@/lib/api/client';
import { cachedConnectionCheck, clearConnectionCheckCache } from '@/lib/api/connectionCheckCache';
import {
  clearShopifyBackfill,
  startShopifyBackfill,
} from '@/lib/commerce/shopifyBackfillTracker';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

export const shopifyApi = {
  async connect(payload: {
    businessId: string | null;
    shop: string;
    accessToken?: string;
    clientId?: string;
    clientSecret?: string;
  }) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/shopify/manual-install`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        ...payload,
        apiVersion: '2025-01',
        plan: 'monthly',
        shopCurrency: 'USD',
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.ok !== true) {
      throw ApiError.fromResult(
        { ...json, statusCode: res.status },
        json?.error || 'Failed to connect Shopify store',
      );
    }
    clearConnectionCheckCache('shopify:');
    startShopifyBackfill(payload.businessId, json?.backfill?.queued);
    return json;
  },

  async listProducts(businessId: string, limit: number = 10) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products?limit=${encodeURIComponent(limit)}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to fetch products');
    }
    return res.json();
  },

  async getLocalProducts(
    businessId: string | undefined,
    limit: number = 20,
    offset: number = 0,
    filters?: { search?: string; status?: string; vendor?: string; type?: string }
  ) {
    if (!businessId) {
      throw new Error('businessId or organizationId is required to fetch Shopify products');
    }
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    if (filters?.search) params.set('search', filters.search);
    if (filters?.status && filters.status !== 'all') params.set('status', filters.status);
    if (filters?.vendor && filters.vendor !== 'all') params.set('vendor', filters.vendor);
    if (filters?.type && filters.type !== 'all') params.set('type', filters.type);
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products/local?${params.toString()}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to fetch local products');
    }
    return res.json();
  },

  async getStoreMeta(businessId: string): Promise<{ vendors: string[]; productTypes: string[]; collections: { id: number; title: string; productsCount?: number }[]; smartCollections: { id: number; title: string; productsCount?: number }[] }> {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/meta`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) return { vendors: [], productTypes: [], collections: [], smartCollections: [] };
    return res.json();
  },

  async searchTaxonomyCategories(
    businessId: string,
    query: string,
    limit = 20,
  ): Promise<{ id: string; name: string; fullName: string; isLeaf: boolean }[]> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (query.trim()) params.set('search', query.trim());
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/taxonomy-categories?${params}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) return [];
    const body = await res.json().catch(() => ({}));
    return Array.isArray(body?.categories) ? body.categories : [];
  },

  async createCollection(
    businessId: string,
    title: string,
  ): Promise<{ id: number; title: string; existing: boolean }> {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/collections`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ title }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...body, statusCode: res.status }, 'Failed to create category');
    }
    const collection = body?.collection;
    if (!collection || collection.id == null) {
      throw new Error('Shopify did not return the new category');
    }
    return {
      id: Number(collection.id),
      title: String(collection.title || title),
      existing: Boolean(collection.existing),
    };
  },

  async getProductCollections(businessId: string, shopifyProductId: string): Promise<number[]> {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products/${shopifyProductId}/collections`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to fetch product categories');
    }
    const body = await res.json().catch(() => ({}));
    return Array.isArray(body?.collectionIds) ? body.collectionIds.map(Number) : [];
  },

  async createProduct(businessId: string, productData: any) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(productData),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to create product');
    }
    return res.json();
  },

  async updateProduct(businessId: string, productId: string, productData: any) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products/${productId}`;
    const res = await fetch(url, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(productData),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to update product');
    }
    return res.json();
  },

  async deleteProduct(businessId: string, productId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products/${productId}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to delete product');
    }
    return res.json();
  },

  async syncProducts(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products/sync`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}), // Send empty object to avoid Fastify empty body error
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to sync products');
    }
    return res.json();
  },

  async syncSingleProduct(businessId: string, shopifyProductId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/products/${encodeURIComponent(shopifyProductId)}/sync`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to sync this product from Shopify');
    }
    return json as { success: boolean; found: boolean; message?: string };
  },

  async getLocalOrders(businessId: string | undefined, limit: number = 20, offset: number = 0, filters?: {
    search?: string;
    financialStatus?: string;
    fulfillmentStatus?: string;
    tag?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) {
    if (!businessId) {
      throw new Error('businessId or organizationId is required to fetch Shopify orders');
    }
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.financialStatus) params.set('financial_status', filters.financialStatus);
    if (filters?.fulfillmentStatus) params.set('fulfillment_status', filters.fulfillmentStatus);
    if (filters?.tag) params.set('tag', filters.tag);
    if (filters?.sortBy) params.set('sort_by', filters.sortBy);
    if (filters?.sortOrder) params.set('sort_order', filters.sortOrder);
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/orders/local?${params}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to fetch local orders');
    }
    return res.json();
  },

  async syncOrders(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/orders/sync`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}), // Send empty object to avoid Fastify empty body error
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to sync orders');
    }
    return res.json();
  },

  async syncCustomers(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/customers/sync`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}), // Send empty object to avoid Fastify empty body error
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to sync customers');
    }
    return res.json();
  },

  async checkConnection(businessId?: string, opts?: { probe?: boolean }) {
    const probe = opts?.probe !== false;
    const cacheKey = `shopify:${businessId ?? 'org'}${probe ? '' : ':stored'}`;
    return cachedConnectionCheck(cacheKey, async () => {
      const base = businessId
        ? `${API_CONFIG.BASE_URL}/shopify/${businessId}/connection`
        : `${API_CONFIG.BASE_URL}/shopify/connection`;
      const url = probe ? base : `${base}?probe=false`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to check connection');
      }
      const data = await res.json();
      return data;
    });
  },

  async listWebhooks(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/webhooks`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to list webhooks');
    }
    const data = await res.json();
    return data.webhooks as Array<{
      id: number;
      address: string;
      topic: string;
      created_at: string;
      updated_at: string;
      format: string;
      api_version: string;
    }>;
  },

  async createWebhook(businessId: string, topic: string, address: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/webhooks`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ topic, address }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to create webhook');
    }
    return res.json();
  },

  async updateWebhook(businessId: string, webhookId: string | number, address: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/webhooks/${webhookId}`;
    const res = await fetch(url, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ address }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to update webhook');
    }
    return res.json();
  },

  async deleteWebhook(businessId: string, webhookId: string | number) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/webhooks/${webhookId}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to delete webhook');
    }
    return res.json();
  },

  async disconnect(businessId?: string | null) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/shopify/disconnect`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        businessId: businessId ?? undefined,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to disconnect Shopify');
    }
    clearConnectionCheckCache('shopify:');
    clearShopifyBackfill(businessId);
    return res.json();
  },

  async syncToCatalog(
    businessAccountId: string,
    businessId: string,
    options?: {
      limit?: number;
      skipExisting?: boolean;
      catalogId?: string;
      shopifyProductId?: string;
    }
  ) {
    const url = `${API_CONFIG.BASE_URL}/commerce/sync/shopify`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        businessAccountId,
        businessId,
        limit: options?.limit || 20,
        skipExisting: options?.skipExisting !== false,
        ...(options?.catalogId ? { catalogId: options.catalogId } : {}),
        ...(options?.shopifyProductId
          ? { shopifyProductId: options.shopifyProductId }
          : {}),
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = ApiError.fromResult(
        { ...json, statusCode: res.status },
        'Failed to sync products to WhatsApp catalog',
      );
      (error as any).errorData = json;
      throw error;
    }
    return json;
  },

  async getLocalCheckouts(
    businessId?: string,
    limit: number = 20,
    offset: number = 0,
    options?: { status?: 'needs_message'; includeRecovered?: boolean },
  ) {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (options?.status) params.set('status', options.status);
    if (businessId && options?.includeRecovered === false) params.set('includeRecovered', 'false');
    const url = businessId
      ? `${API_CONFIG.BASE_URL}/shopify/${businessId}/checkouts?${params}`
      : `${API_CONFIG.BASE_URL}/shopify/checkouts/local?${params}`;

    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      if (res.status === 404) return { checkouts: [] };
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to fetch checkouts');
    }
    const data = await res.json();
    return data;
  },

  async updateOrderTags(businessId: string, orderId: string, tags: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/orders/${orderId}/tags`;
    const res = await fetch(url, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tags }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to update order tags');
    }
    return res.json();
  },

  async listOrderTags(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/order-tags`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to fetch order tags');
    }
    return res.json();
  },

  async createOrderTag(businessId: string, tagName: string, isDefault = false) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/order-tags`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tagName, isDefault }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to create order tag');
    }
    return res.json();
  },

  async deleteOrderTag(businessId: string, tagId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/order-tags/${tagId}`;
    const res = await fetch(url, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to delete order tag');
    }
    return res.json();
  },

  async setDefaultOrderTag(businessId: string, tagId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/order-tags/${tagId}/default`;
    const res = await fetch(url, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to set default order tag');
    }
    return res.json();
  },

  async unsetDefaultOrderTag(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/order-tags/default`;
    const res = await fetch(url, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to clear default order tag');
    }
    return res.json();
  },

  async syncCheckouts(businessId: string) {
    const url = `${API_CONFIG.BASE_URL}/shopify/${businessId}/checkouts/sync`;
    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}), // Send empty object to avoid Fastify empty body error
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw ApiError.fromResult({ ...err, statusCode: res.status }, 'Failed to sync checkouts');
    }
    return res.json();
  },
};

export function formatCurrency(amount: number, currencyCode: string = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (e) {
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
}

export function getCurrencySymbol(currencyCode: string = 'USD'): string {
  const code = (currencyCode || 'USD').toUpperCase();
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    const symbol = parts.find(p => p.type === 'currency')?.value;
    return symbol || code;
  } catch {
    return code;
  }
}
